import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import axios from 'axios';
import { setPageMeta, breadcrumbLd } from '../utils/seo';
import { getTrade } from '../data/hireTrades';
import { getCity, HIRE_CITIES } from '../data/hireCities';
import { HIRE_TRADES } from '../data/hireTrades';
import { CheckCircle, CaretDown, UsersThree, Briefcase, CurrencyDollar, ArrowRight, MapPin } from '@phosphor-icons/react';
import AdBanner from '../components/AdBanner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const an = (w) => (/^[aeiou]/i.test(w) ? 'an' : 'a');
const cap = (w) => w.replace(/\b\w/g, (c) => c.toUpperCase());

const Faq = ({ f, i }) => {
  const [open, setOpen] = useState(i === 0);
  return (
    <div className="border-2 border-neutral-900 dark:border-neutral-700 bg-white dark:bg-neutral-800">
      <button onClick={() => setOpen(!open)} className="w-full flex items-center justify-between gap-3 p-4 text-left" data-testid={`city-faq-${i}`}>
        <span className="font-black text-neutral-900 dark:text-white">{f.q}</span>
        <CaretDown size={18} weight="bold" className={`shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && <p className="px-4 pb-4 text-neutral-600 dark:text-neutral-300 text-sm leading-relaxed">{f.a}</p>}
    </div>
  );
};

export default function HireTradeCityPage() {
  const { slug, citySlug } = useParams();
  const trade = getTrade(slug);
  const city = getCity(citySlug);
  const [stats, setStats] = useState(null);

  useEffect(() => {
    if (!trade || !city) return;
    window.scrollTo(0, 0);
    setStats(null);
    axios.get(`${API_URL}/api/trade-stats`, { params: { terms: trade.searchTerms, city: city.name } })
      .then((r) => setStats(r.data)).catch(() => {});
    const loc = `${city.name}, ${city.abbr}`;
    setPageMeta({
      title: `Hire ${an(trade.singular)} ${cap(trade.singular)} in ${loc} — Local ${trade.trade} Pros | ConstructHelp`,
      description: `Find and hire vetted ${trade.trade.toLowerCase()} pros in ${loc}. Compare free local quotes and verified reviews, and pay safely with milestone escrow on ConstructHelp.`,
      url: `${window.location.origin}/hire/${trade.slug}/${city.slug}`,
      jsonLd: [
        {
          '@context': 'https://schema.org',
          '@type': 'FAQPage',
          mainEntity: cityFaqs(trade, city).map((f) => ({
            '@type': 'Question', name: f.q,
            acceptedAnswer: { '@type': 'Answer', text: f.a },
          })),
        },
        breadcrumbLd([
          { name: 'Home', url: window.location.origin },
          { name: 'Hire a Pro', url: `${window.location.origin}/hire` },
          { name: trade.name, url: `${window.location.origin}/hire/${trade.slug}` },
          { name: loc },
        ]),
      ],
    });
  }, [slug, citySlug]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!trade || !city) {
    return (
      <div className="min-h-screen bg-[#E8E9EB] dark:bg-[#121212] flex items-center justify-center">
        <div className="text-center">
          <p className="font-black text-xl text-neutral-900 dark:text-white mb-4">Page not found</p>
          <Link to="/hire" className="text-[#D95800] font-bold underline">Browse all trades</Link>
        </div>
      </div>
    );
  }

  const loc = `${city.name}, ${city.abbr}`;
  const faqs = cityFaqs(trade, city);
  const otherCities = HIRE_CITIES.filter((c) => c.slug !== city.slug).slice(0, 12);
  const otherTrades = HIRE_TRADES.filter((t) => t.slug !== trade.slug).slice(0, 8);

  return (
    <div className="min-h-screen bg-[#E8E9EB] dark:bg-[#121212]" data-testid="hire-city-page">
      <div className="bg-neutral-900 border-b-4 border-[#D95800]">
        <div className="max-w-5xl mx-auto px-4 py-12 grid lg:grid-cols-5 gap-8 items-center">
          <div className="lg:col-span-3">
            <nav className="text-xs text-neutral-400 font-bold mb-4">
              <Link to="/" className="hover:text-white">Home</Link> / <Link to="/hire" className="hover:text-white">Hire a Pro</Link> / <Link to={`/hire/${trade.slug}`} className="hover:text-white" data-testid="city-trade-breadcrumb">{trade.name}</Link> / <span className="text-white">{loc}</span>
            </nav>
            <h1 className="text-4xl sm:text-5xl font-black text-white uppercase leading-tight mb-4">
              Hire {an(trade.singular)} {cap(trade.singular)} in {city.name}
            </h1>
            <p className="text-neutral-300 max-w-3xl leading-relaxed">
              Looking for {trade.trade.toLowerCase()} help in {loc}? ConstructHelp connects {city.name} homeowners
              with verified local {trade.name.toLowerCase()} who bid on your project — compare quotes, reviews, and
              timelines side by side, then pay through milestone escrow so money is only released as work is approved.
            </p>
            <div className="flex flex-wrap gap-3 mt-6">
              <Link to="/get-quotes" className="h-12 px-6 flex items-center gap-2 bg-[#D95800] hover:bg-[#E55D00] text-white font-black uppercase border-2 border-white shadow-[3px_3px_0px_rgba(255,255,255,0.3)]" data-testid="city-get-quotes-btn">
                Get 3 Free Quotes <ArrowRight size={18} weight="bold" />
              </Link>
              <Link to="/find-workers" className="h-12 px-6 flex items-center gap-2 bg-transparent text-white font-black uppercase border-2 border-white hover:bg-white/10" data-testid="city-browse-pros-btn">
                Browse {city.name} Pros
              </Link>
            </div>
          </div>
          <div className="hidden lg:block lg:col-span-2">
            <img src={`/hire/${trade.slug}.jpg`} alt={`${trade.trade} pro working on a residential project in ${loc}`}
              className="w-full aspect-[4/3] object-cover border-2 border-white shadow-[6px_6px_0px_#D95800]"
              loading="eager" data-testid="city-trade-photo" />
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 py-10 space-y-10">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-testid="city-stats">
          <div className="bg-white dark:bg-neutral-800 border-2 border-neutral-900 dark:border-neutral-700 p-4 shadow-[3px_3px_0px_#000]">
            <UsersThree size={20} weight="bold" className="text-[#D95800]" />
            <p className="text-xl font-black mt-1 dark:text-white">{stats ? (stats.pros >= 3 ? stats.pros : stats.total_pros) : '—'}</p>
            <p className="text-xs font-bold uppercase text-neutral-500">Verified pros on ConstructHelp</p>
          </div>
          <div className="bg-white dark:bg-neutral-800 border-2 border-neutral-900 dark:border-neutral-700 p-4 shadow-[3px_3px_0px_#000]">
            <Briefcase size={20} weight="bold" className="text-[#D95800]" />
            {stats && stats.open_jobs === 0 ? (
              <Link to="/get-quotes" className="block" data-testid="city-first-job-cta">
                <p className="text-sm font-black mt-1 text-[#D95800] leading-snug underline">Post the first {trade.trade.toLowerCase()} job in {city.name} — free</p>
                <p className="text-xs font-bold uppercase text-neutral-500">Pros respond fast</p>
              </Link>
            ) : (
              <>
                <p className="text-xl font-black mt-1 dark:text-white">{stats ? stats.open_jobs : '—'}</p>
                <p className="text-xs font-bold uppercase text-neutral-500">Open {trade.trade.toLowerCase()} jobs</p>
              </>
            )}
          </div>
          <div className="bg-white dark:bg-neutral-800 border-2 border-neutral-900 dark:border-neutral-700 p-4 shadow-[3px_3px_0px_#000]">
            <CurrencyDollar size={20} weight="bold" className="text-[#D95800]" />
            <p className="text-sm font-black mt-1 dark:text-white leading-snug">{trade.avgCost}</p>
            <p className="text-xs font-bold uppercase text-neutral-500">Typical 2026 pricing</p>
          </div>
        </div>

        <AdBanner placement="hire" trade={trade.slug} city={city.slug} />

        <div>
          <h2 className="text-lg font-black uppercase text-neutral-900 dark:text-white mb-4">Before you hire {an(trade.singular)} {trade.singular} in {city.name}</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {trade.tips.map((t, i) => (
              <div key={i} className="flex items-start gap-3 bg-white dark:bg-neutral-800 border-2 border-neutral-900 dark:border-neutral-700 p-4">
                <CheckCircle size={20} weight="fill" className="text-[#D95800] shrink-0 mt-0.5" />
                <p className="text-sm text-neutral-700 dark:text-neutral-300">{t}</p>
              </div>
            ))}
          </div>
        </div>

        {trade.costGuideSlug && (
          <Link to={`/guides/${trade.costGuideSlug}`}
            className="block bg-neutral-900 border-2 border-neutral-900 p-5 shadow-[4px_4px_0px_#D95800] hover:translate-x-0.5 transition-transform"
            data-testid="city-cost-guide-link">
            <p className="text-xs font-black uppercase text-[#D95800] mb-1">2026 Cost Guide</p>
            <p className="font-black text-white">How much does {trade.trade.toLowerCase()} work cost? See the full price breakdown →</p>
          </Link>
        )}

        <div>
          <h2 className="text-lg font-black uppercase text-neutral-900 dark:text-white mb-4">{trade.name} in {loc} — FAQs</h2>
          <div className="space-y-3">
            {faqs.map((f, i) => <Faq key={i} f={f} i={i} />)}
          </div>
        </div>

        <div className="bg-[#D95800] border-2 border-neutral-900 p-8 text-center shadow-[5px_5px_0px_#000]">
          <h2 className="text-2xl font-black uppercase text-white mb-2">Ready to hire {an(trade.singular)} {trade.singular} in {city.name}?</h2>
          <p className="text-orange-100 mb-5">Post your project once — verified {loc} pros send you free quotes. Pay safely with milestone escrow.</p>
          <Link to="/get-quotes" className="inline-flex h-12 px-8 items-center bg-neutral-900 text-white font-black uppercase border-2 border-white" data-testid="city-cta-bottom">
            Get Free Quotes
          </Link>
        </div>

        <div>
          <h2 className="text-sm font-black uppercase text-neutral-500 mb-3 flex items-center gap-1"><MapPin size={14} weight="bold" /> {trade.name} in other cities</h2>
          <div className="flex flex-wrap gap-2" data-testid="city-siblings">
            {otherCities.map((c) => (
              <Link key={c.slug} to={`/hire/${trade.slug}/${c.slug}`}
                className="px-3 py-2 bg-white dark:bg-neutral-800 border-2 border-neutral-900 dark:border-neutral-700 text-sm font-bold text-neutral-800 dark:text-neutral-200 hover:bg-orange-50">
                {c.name}, {c.abbr}
              </Link>
            ))}
          </div>
        </div>

        <div>
          <h2 className="text-sm font-black uppercase text-neutral-500 mb-3">Other pros in {city.name}</h2>
          <div className="flex flex-wrap gap-2" data-testid="trade-siblings">
            {otherTrades.map((t) => (
              <Link key={t.slug} to={`/hire/${t.slug}/${city.slug}`}
                className="px-3 py-2 bg-white dark:bg-neutral-800 border-2 border-neutral-900 dark:border-neutral-700 text-sm font-bold text-neutral-800 dark:text-neutral-200 hover:bg-orange-50">
                {t.name}
              </Link>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function cityFaqs(trade, city) {
  const loc = `${city.name}, ${city.abbr}`;
  return [
    {
      q: `How much does it cost to hire ${an(trade.singular)} ${trade.singular} in ${loc}?`,
      a: `Typical 2026 pricing runs ${trade.avgCost}. ${city.name} rates can sit above or below the national range depending on labor demand — collecting 2–3 local bids on ConstructHelp is the fastest way to see the real market price for your project.`,
    },
    {
      q: `How do I find a good ${trade.singular} near me in ${city.name}?`,
      a: `Check for a valid ${city.state} license where required, active liability insurance, and local reviews with project photos. On ConstructHelp, ${city.name} ${trade.name.toLowerCase()} profiles show verified reviews, past jobs, and response times so you can compare before contacting anyone.`,
    },
    {
      q: `Do ${trade.name.toLowerCase()} need a license in ${city.state}?`,
      a: `Licensing rules vary by state and municipality — many trades require state or city registration in ${city.state}, and permits are commonly required for larger projects. A reputable pro will name their license/registration and handle permits without being asked.`,
    },
    ...trade.faqs.slice(3, 4),
  ];
}
